// SVG path-data parser + flattener. Parses the full command set
// (M/L/H/V/C/S/Q/T/A + Z, absolute and relative) and flattens every curve to
// short line segments with a fixed tolerance in SVG user units, so downstream
// code only ever deals with polylines. R12 has no spline/ellipse entities, so
// this is the fidelity floor for the whole export.

/** Max chord-deviation of a flattened curve, in SVG user units (viewBoxes are ~500 units). */
export const FLATTEN_TOLERANCE = 0.25;

const MAX_DEPTH = 18;

const COMMAND_RE = /([MmLlHhVvCcSsQqTtAaZz])([^MmLlHhVvCcSsQqTtAaZz]*)/g;
const NUMBER_AT_RE = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/;
const isSeparator = (c) => c === ' ' || c === '\t' || c === '\n' || c === '\r' || c === ',';
const absolutePoint = (rel, current, vx, vy) =>
  rel ? { x: current.x + vx, y: current.y + vy } : { x: vx, y: vy };

/**
 * Tokenize a command's argument text. The SVG grammar makes the two arc flags
 * single characters, so "0110 10" is largeArc=0 sweep=1 x=10 y=10 — a plain
 * number regex would swallow "0110" as one number and drop the arc.
 * `flagPositions` marks the per-repetition argument slots parsed as flags.
 */
function scanArgs(text, arity, flagPositions) {
  const out = [];
  let i = 0;
  while (i < text.length) {
    while (i < text.length && isSeparator(text[i])) i++;
    if (i >= text.length) break;
    if (flagPositions?.has(out.length % arity)) {
      if (text[i] !== '0' && text[i] !== '1') break; // malformed flag — stop like a browser would
      out.push(Number(text[i]));
      i++;
      continue;
    }
    const match = NUMBER_AT_RE.exec(text.slice(i));
    if (!match) break;
    out.push(Number(match[0]));
    i += match[0].length;
  }
  return out;
}

const ARC_FLAG_POSITIONS = new Set([3, 4]);

/** Distance from `p` to the segment a–b (falls back to distance-to-a when degenerate). */
function distToSegment(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function flattenCubicInto(out, p0, p1, p2, p3, tol, depth = 0) {
  if (depth >= MAX_DEPTH || (distToSegment(p1, p0, p3) <= tol && distToSegment(p2, p0, p3) <= tol)) {
    out.push(p3);
    return;
  }
  // de Casteljau split at t = 0.5
  const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const p01 = mid(p0, p1);
  const p12 = mid(p1, p2);
  const p23 = mid(p2, p3);
  const p012 = mid(p01, p12);
  const p123 = mid(p12, p23);
  const m = mid(p012, p123);
  flattenCubicInto(out, p0, p01, p012, m, tol, depth + 1);
  flattenCubicInto(out, m, p123, p23, p3, tol, depth + 1);
}

function flattenQuadraticInto(out, p0, q, p1, tol) {
  // Exact degree elevation to a cubic, then reuse the cubic flattener.
  const c1 = { x: p0.x + (2 / 3) * (q.x - p0.x), y: p0.y + (2 / 3) * (q.y - p0.y) };
  const c2 = { x: p1.x + (2 / 3) * (q.x - p1.x), y: p1.y + (2 / 3) * (q.y - p1.y) };
  flattenCubicInto(out, p0, c1, c2, p1, tol);
}

/** SVG spec F.6.5: endpoint parameterization → center parameterization, then sample. */
function flattenArcInto(
  out,
  p0,
  rxIn,
  ryIn,
  xAxisRotationDeg,
  largeArc,
  sweep,
  p1,
  tol,
) {
  if (p0.x === p1.x && p0.y === p1.y) return;
  let rx = Math.abs(rxIn);
  let ry = Math.abs(ryIn);
  if (rx === 0 || ry === 0) {
    out.push(p1);
    return;
  }
  const phi = (xAxisRotationDeg * Math.PI) / 180;
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);
  const dx2 = (p0.x - p1.x) / 2;
  const dy2 = (p0.y - p1.y) / 2;
  const x1p = cosPhi * dx2 + sinPhi * dy2;
  const y1p = -sinPhi * dx2 + cosPhi * dy2;
  // Scale radii up if they cannot span the endpoints (F.6.6.3).
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lambda > 1) {
    const s = Math.sqrt(lambda);
    rx *= s;
    ry *= s;
  }
  const rxSq = rx * rx;
  const rySq = ry * ry;
  const num = rxSq * rySq - rxSq * y1p * y1p - rySq * x1p * x1p;
  const den = rxSq * y1p * y1p + rySq * x1p * x1p;
  const coef = (largeArc !== sweep ? 1 : -1) * Math.sqrt(Math.max(0, num / den));
  const cxp = coef * ((rx * y1p) / ry);
  const cyp = coef * (-(ry * x1p) / rx);
  const cx = cosPhi * cxp - sinPhi * cyp + (p0.x + p1.x) / 2;
  const cy = sinPhi * cxp + cosPhi * cyp + (p0.y + p1.y) / 2;

  const angleOf = (ux, uy) => Math.atan2(uy, ux);
  const theta1 = angleOf((x1p - cxp) / rx, (y1p - cyp) / ry);
  let dTheta = angleOf((-x1p - cxp) / rx, (-y1p - cyp) / ry) - theta1;
  if (!sweep && dTheta > 0) dTheta -= 2 * Math.PI;
  else if (sweep && dTheta < 0) dTheta += 2 * Math.PI;

  // Sample so the chord deviation stays within tolerance: r(1 - cos(step/2)) <= tol.
  const rMax = Math.max(rx, ry);
  const maxStep = Math.min(Math.PI / 8, 2 * Math.acos(Math.max(-1, Math.min(1, 1 - tol / rMax))));
  const steps = Math.max(2, Math.ceil(Math.abs(dTheta) / Math.max(maxStep, 1e-6)));
  for (let i = 1; i < steps; i++) {
    const theta = theta1 + (dTheta * i) / steps;
    const x = cx + rx * Math.cos(theta) * cosPhi - ry * Math.sin(theta) * sinPhi;
    const y = cy + rx * Math.cos(theta) * sinPhi + ry * Math.sin(theta) * cosPhi;
    out.push({ x, y });
  }
  // Land exactly on the command's endpoint (no trig float dust).
  out.push({ ...p1 });
}

const samePoint = (a, b) => Math.abs(a.x - b.x) < 1e-9 && Math.abs(a.y - b.y) < 1e-9;

/** Drop consecutive duplicate points (degenerate zero-length segments). */
export function dedupePoints(points) {
  const out = [];
  for (const p of points) {
    if (out.length === 0 || !samePoint(out[out.length - 1], p)) out.push(p);
  }
  return out;
}

/**
 * Parse + flatten SVG path data into subpath polylines (SVG user space).
 * Malformed trailing data is ignored, matching browser leniency.
 */
export function flattenPathData(d, tolerance = FLATTEN_TOLERANCE) {
  const subPaths = [];
  let points = [];
  let closed = false;

  let current = { x: 0, y: 0 };
  let subStart = { x: 0, y: 0 };
  let prevCubicCtrl = null;
  let prevQuadCtrl = null;

  const finishSubPath = () => {
    const pts = dedupePoints(points);
    if (pts.length >= 2) subPaths.push({ points: pts, closed });
    points = [];
    closed = false;
  };

  for (const match of String(d ?? '').matchAll(COMMAND_RE)) {
    const cmd = match[1];
    const rel = cmd === cmd.toLowerCase();
    const upper = cmd.toUpperCase();

    // Argument count per repetition; commands may repeat their args implicitly.
    const arity = { M: 2, L: 2, H: 1, V: 1, C: 6, S: 4, Q: 4, T: 2, A: 7, Z: 0 }[upper] ?? 0;
    const args = arity > 0 ? scanArgs(match[2], arity, upper === 'A' ? ARC_FLAG_POSITIONS : null) : [];

    if (upper === 'Z') {
      if (points.length >= 2) {
        points.push({ ...subStart });
        closed = true;
        finishSubPath();
      } else {
        points = [];
      }
      current = { ...subStart };
      // A new segment after Z continues from subStart with an implicit move.
      points = [{ ...current }];
      prevCubicCtrl = null;
      prevQuadCtrl = null;
      continue;
    }

    for (let i = 0; i + arity <= args.length || (arity === 0 && i === 0); i += Math.max(arity, 1)) {
      if (arity === 0) break;
      const a = args.slice(i, i + arity);
      let isCurveC = false;
      let isCurveQ = false;

      switch (upper) {
        case 'M': {
          const p = absolutePoint(rel, current, a[0], a[1]);
          if (i === 0) {
            finishSubPath();
            subStart = p;
            points = [p];
          } else {
            // Implicit LineTo after the first pair.
            points.push(p);
          }
          current = p;
          break;
        }
        case 'L': {
          const p = absolutePoint(rel, current, a[0], a[1]);
          points.push(p);
          current = p;
          break;
        }
        case 'H': {
          const p = rel ? { x: current.x + a[0], y: current.y } : { x: a[0], y: current.y };
          points.push(p);
          current = p;
          break;
        }
        case 'V': {
          const p = rel ? { x: current.x, y: current.y + a[0] } : { x: current.x, y: a[0] };
          points.push(p);
          current = p;
          break;
        }
        case 'C': {
          const c1 = absolutePoint(rel, current, a[0], a[1]);
          const c2 = absolutePoint(rel, current, a[2], a[3]);
          const p = absolutePoint(rel, current, a[4], a[5]);
          flattenCubicInto(points, current, c1, c2, p, tolerance);
          prevCubicCtrl = c2;
          current = p;
          isCurveC = true;
          break;
        }
        case 'S': {
          const c1 = prevCubicCtrl
            ? { x: 2 * current.x - prevCubicCtrl.x, y: 2 * current.y - prevCubicCtrl.y }
            : { ...current };
          const c2 = absolutePoint(rel, current, a[0], a[1]);
          const p = absolutePoint(rel, current, a[2], a[3]);
          flattenCubicInto(points, current, c1, c2, p, tolerance);
          prevCubicCtrl = c2;
          current = p;
          isCurveC = true;
          break;
        }
        case 'Q': {
          const q = absolutePoint(rel, current, a[0], a[1]);
          const p = absolutePoint(rel, current, a[2], a[3]);
          flattenQuadraticInto(points, current, q, p, tolerance);
          prevQuadCtrl = q;
          current = p;
          isCurveQ = true;
          break;
        }
        case 'T': {
          const q = prevQuadCtrl
            ? { x: 2 * current.x - prevQuadCtrl.x, y: 2 * current.y - prevQuadCtrl.y }
            : { ...current };
          const p = absolutePoint(rel, current, a[0], a[1]);
          flattenQuadraticInto(points, current, q, p, tolerance);
          prevQuadCtrl = q;
          current = p;
          isCurveQ = true;
          break;
        }
        case 'A': {
          const p = absolutePoint(rel, current, a[5], a[6]);
          flattenArcInto(points, current, a[0], a[1], a[2], a[3] !== 0, a[4] !== 0, p, tolerance);
          current = p;
          break;
        }
        default:
          break;
      }
      if (!isCurveC) prevCubicCtrl = null;
      if (!isCurveQ) prevQuadCtrl = null;
    }
  }
  finishSubPath();
  return subPaths;
}
